import { carrierMediumText, type CarrierMedium } from "kontrolEdge/helpers/carrierAccess";

/** A link of a CPE's router, as the detail serves it. */
interface CpeLink {
  kind: "backbone-uplink" | "ha-interconnect";
  capacity: string | null;
  destination: string | null;
  carrierCode: string | null;
  carrierName: string | null;
  portAllocationId: number;
}

/** A carrier leg of the CPE, as the detail serves it. */
interface CpeLeg<Status> {
  id: number;
  portAllocationId?: number | null;
  carrierCode: string;
  name: string;
  capacity: string | null;
  medium?: CarrierMedium | null;
  status?: Status;
  statusMessage?: string | null;
  carrierAccess?: { id: number; serviceId: string } | null;
}

/** One row of the CPE's links table (CTX-801, SD-WAN style). */
export interface CpeLinkRow<Status> {
  key: string;
  /** What the row is: a backbone uplink, a carrier on no listed link, or the HA link. */
  kind: "uplink" | "carrier" | "ha-interconnect";
  /** The link the row is (its bandwidth chart); null for a carrier on no listed link. */
  linkId: number | null;
  label: string;
  speed: string | null;
  carrier: string | null;
  /** "Fiber Optic" / "Satellite"; null on a link with no carrier, or not captured. */
  medium: string | null;
  /** The carrier's status; undefined on a link with no carrier. */
  status?: Status;
  statusMessage: string | null;
  carrierAccess: { id: number; serviceId: string } | null;
}

/**
 * The CPE's links with each carrier's status in the same row, as SD-WAN
 * shows its branches: an uplink takes the leg on its interface for its
 * carrier; the HA interconnect has no carrier; a leg on no listed link (a
 * last mile, a manual leg) gets a row of its own, so no carrier is left out.
 */
export function cpeLinkRows<Status>(
  links: CpeLink[],
  legs: CpeLeg<Status>[]
): CpeLinkRow<Status>[] {
  const used = new Set<number>();
  const legFor = (link: CpeLink) => {
    const onInterface = legs.filter(
      (leg) => !used.has(leg.id) && leg.portAllocationId === link.portAllocationId
    );
    // The same carrier can be named twice on one interface (a CARR_ and an
    // LM marker); the one a Carrier Access claimed speaks for the link.
    const claimedFirst = (candidates: CpeLeg<Status>[]) =>
      candidates.find((leg) => leg.carrierAccess) ?? candidates[0];
    return (
      claimedFirst(
        onInterface.filter((leg) => leg.carrierCode === link.carrierCode)
      ) ?? claimedFirst(onInterface)
    );
  };

  const fromLeg = (leg: CpeLeg<Status>) => ({
    carrier: leg.name,
    medium: carrierMediumText(leg.medium),
    status: leg.status,
    statusMessage: leg.statusMessage ?? null,
    carrierAccess: leg.carrierAccess ?? null,
  });

  const rows: CpeLinkRow<Status>[] = [];
  const uplinks = links.filter((link) => link.kind === "backbone-uplink");
  const interconnects = links.filter((link) => link.kind === "ha-interconnect");

  for (const link of uplinks) {
    const leg = legFor(link);
    if (leg) used.add(leg.id);
    rows.push({
      key: `link-${link.portAllocationId}`,
      kind: "uplink",
      linkId: link.portAllocationId,
      label: `Uplink${link.destination ? ` — ${link.destination}` : ""}`,
      speed: link.capacity,
      ...(leg
        ? fromLeg(leg)
        : {
            carrier: link.carrierName,
            medium: null,
            statusMessage: null,
            carrierAccess: null,
          }),
    });
  }

  for (const leg of legs.filter((candidate) => !used.has(candidate.id))) {
    rows.push({
      key: `leg-${leg.id}`,
      kind: "carrier",
      linkId: null,
      label: "Carrier access",
      speed: leg.capacity,
      ...fromLeg(leg),
    });
  }

  for (const link of interconnects) {
    rows.push({
      key: `link-${link.portAllocationId}`,
      kind: "ha-interconnect",
      linkId: link.portAllocationId,
      label: "HA interconnect",
      speed: link.capacity,
      carrier: null,
      medium: null,
      statusMessage: null,
      carrierAccess: null,
    });
  }

  return rows;
}

/**
 * The rows a CPE shows once it has a sold Carrier Access: only the links of
 * that access, plus the HA interconnect (the link between the pair, not a
 * carrier). A CPE on a router that also carries backbone trunks would
 * otherwise list every trunk carrier. Before any access is declared, every
 * row shows.
 */
export function soldCpeLinkRows<Status>(
  rows: CpeLinkRow<Status>[]
): CpeLinkRow<Status>[] {
  if (!rows.some((row) => row.carrierAccess)) return rows;
  return rows.filter(
    (row) => row.carrierAccess || row.kind === "ha-interconnect"
  );
}
